import { APIBASE_REVIVERAUTHENTICATION, axios, refreshTokenAxios } from "./ApiConfig";
import {
  getToken,
  getUserContextIds,
  removeRedirection,
  removeUserContextIds,
  removeUserToken,
} from "@/utils/sessionStorage";
import type { AxiosError, AxiosRequestConfig, AxiosResponse } from "axios";

type RetryableConfig = AxiosRequestConfig & { _retry?: boolean };

let refreshPromise: Promise<string> | null = null;
let interceptorId: number | null = null;
let isLoggingOut = false;

const forceLogout = (logOut: () => void) => {
  if (isLoggingOut) return;
  isLoggingOut = true;
  // Clear persisted session synchronously so the post-reload boot lands on the
  // login screen instead of re-entering the authenticated shell with stale state.
  removeUserToken();
  removeUserContextIds();
  removeRedirection();
  try {
    logOut();
  } catch {
    // logOut may hit the network and reject; the storage wipe + redirect below
    // is what actually recovers the user.
  }
  window.location.replace("/login");
};

const refreshAccessToken = async (token: string, userId: number | string): Promise<string> => {
  const url = `${APIBASE_REVIVERAUTHENTICATION}api/authentication/refresh/user/${userId}`;
  const response: AxiosResponse = await refreshTokenAxios.post(url, null, {
    headers: { Authorization: token },
  });
  const newToken = (response.headers.authorization ?? response.headers.Authorization) as string | undefined;
  if (!newToken) throw new Error("no token returned from refresh");
  localStorage.setItem("token", JSON.stringify(newToken));
  return newToken;
};

export const setupRefreshTokenInterceptor = (logOut: () => void): void => {
  if (interceptorId !== null) {
    axios.interceptors.response.eject(interceptorId);
  }
  // A fresh setup call means a new session (post-login, or post-company-load) — the
  // "already forcing logout" latch from a prior session must not carry over, otherwise
  // every 401 after the first ever logout silently no-ops instead of forcing one.
  isLoggingOut = false;
  interceptorId = axios.interceptors.response.use(
    (res) => res,
    async (error: AxiosError) => {
      const originalConfig = error.config as RetryableConfig | undefined;
      const isLoginRequest = originalConfig?.url?.includes("login");
      if (isLoginRequest || error.response?.status !== 401) {
        return Promise.reject(error);
      }

      // Prevent infinite retry loops: if this request already went through a
      // refresh cycle and still returned 401, the endpoint has an auth issue
      // unrelated to token expiry — propagate the error without forcing logout.
      if (originalConfig?._retry) {
        return Promise.reject(error);
      }

      const token = getToken();
      const ids = getUserContextIds();
      if (!token || !ids?.userId) {
        forceLogout(logOut);
        return Promise.reject(error);
      }

      if (!refreshPromise) {
        refreshPromise = refreshAccessToken(token, ids.userId).finally(() => {
          refreshPromise = null;
        });
      }

      try {
        const newToken = await refreshPromise;
        if (!originalConfig) return Promise.reject(error);
        originalConfig._retry = true;
        originalConfig.headers = originalConfig.headers ?? {};
        (originalConfig.headers as Record<string, string>).Authorization = newToken;
        return axios(originalConfig);
      } catch {
        forceLogout(logOut);
        return Promise.reject(error);
      }
    }
  );
};
